import clsx from 'clsx';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CellRendererProps } from '../../../DataTable.types';
import { truncateByCharacters } from '../../../../../../utils/utils';
import Tooltip from '../../../../Tooltip/Tooltip';
import CaretIcon from '../../../../../../assets/Svg/CaretIcon';
import { COLORS } from '../../../../../../utils/constants/colors';
import MultiSelectWithDisabledOptions from '../../../../MultiSelectDropdown/MultiSelectWithDisabledOptions';
import { useOutsideClick } from '../../../../../../utils/constants/hooks';
import styles from './MultiSelectDropdownCellRenderer.module.scss';

export interface UpdateMultiSelectDropdownProps {
    row?: any;
    column?: any;
    selectedValues?: string[];
}

interface Option {
    label: string;
    value: string;
}

export const MultiSelectDropdownCellRenderer: React.FC<CellRendererProps> = ({ value, row, column, dynamicProps }) => {
    const { updateMultiSelectDropdown, isDisabled } = dynamicProps ?? {};
    const { dropdownOptions } = column;

    // Track selected values (should be an array from the row data)
    const [selectedValues, setSelectedValues] = useState<string[]>([]);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement | null>(null);
    const selectedValuesRef = useRef<string[]>(selectedValues);
    const isDropdownOpenRef = useRef<boolean>(isDropdownOpen);
    const initialValuesRef = useRef<string[]>([]); // Track initial values when dropdown opens

    // Initialize selected values from the row data
    useEffect(() => {
        if (Array.isArray(value)) {
            setSelectedValues(value);
        } else if (value) {
            // Handle single value as array
            setSelectedValues([value]);
        } else {
            setSelectedValues([]);
        }
    }, [value]);

    useEffect(() => {
        selectedValuesRef.current = selectedValues;
    }, [selectedValues]);

    useEffect(() => {
        const wasOpen = isDropdownOpenRef.current;
        isDropdownOpenRef.current = isDropdownOpen;
        // Store initial values only when dropdown first opens (not on subsequent changes)
        if (isDropdownOpen && !wasOpen) {
            initialValuesRef.current = [...selectedValues];
        }
    }, [isDropdownOpen, selectedValues]);

    // Close dropdown when clicking outside and save changes (only if values changed)
    useOutsideClick(containerRef, () => {
        if (isDropdownOpenRef.current) {
            setIsDropdownOpen(false);
            // Only call update handler if values have changed
            const hasChanged =
                initialValuesRef.current.length !== selectedValuesRef.current.length ||
                !initialValuesRef.current.every((val) => selectedValuesRef.current.includes(val)) ||
                !selectedValuesRef.current.every((val) => initialValuesRef.current.includes(val));

            if (hasChanged && updateMultiSelectDropdown) {
                updateMultiSelectDropdown({
                    row,
                    column,
                    selectedValues: selectedValuesRef.current,
                });
            }
        }
    });

    const handleToggleSelection = (newSelected: string[]) => {
        setSelectedValues(newSelected);
    };

    // Convert dropdownOptions object to array format for MultiSelectWithDisabledOptions
    const allOptions: Option[] = useMemo(() => {
        if (!dropdownOptions) return [];
        return Object.values(dropdownOptions).map((option: any) => ({
            label: option.label,
            value: option.value,
        }));
    }, [dropdownOptions]);

    // Get selected option objects with their styling
    const selectedOptions = useMemo(() => {
        if (!dropdownOptions || !selectedValues.length) return [];
        return selectedValues.map((val) => dropdownOptions[val]).filter(Boolean);
    }, [selectedValues, dropdownOptions]);

    if (!dropdownOptions) {
        return <div style={{ color: COLORS.GREY }}>-</div>;
    }

    if (!updateMultiSelectDropdown) {
        return null;
    }

    const firstName = selectedOptions[0]?.label ? truncateByCharacters(selectedOptions[0].label, 20) : '';
    const remainingOptions = selectedOptions.slice(1);
    const firstOption = selectedOptions[0];

    return (
        <div ref={containerRef}>
            <div
                className={styles.MultiSelectContainer}
                onClick={(e) => {
                    e.stopPropagation();
                    if (isDisabled) return;
                    setIsDropdownOpen((prev) => !prev);
                }}
                style={{
                    backgroundColor: firstOption?.backgroundColor || '#f5f5f5',
                    cursor: isDisabled ? 'not-allowed' : 'pointer',
                    opacity: isDisabled ? 0.6 : 1,
                }}
            >
                {selectedOptions.length > 0 ? (
                    <Tooltip
                        direction="bottom"
                        style={{ width: 'fit-content', left: '-30px' }}
                        content={
                            <ul style={{ margin: 0, paddingLeft: '1em' }}>
                                {selectedOptions.map((option, index) => (
                                    <li key={index}>{option.label}</li>
                                ))}
                            </ul>
                        }
                    >
                        <span className={styles.MultiSelectText} style={{ color: firstOption?.labelColor || '#000000' }}>
                            {firstName}
                        </span>
                        {remainingOptions.length > 0 && <span className={styles.More}> +{remainingOptions.length} more</span>}
                    </Tooltip>
                ) : (
                    <span className={styles.MultiSelectText} style={{ color: firstOption?.labelColor || '#b5b5b5' }}>
                        Select
                    </span>
                )}
                {!isDisabled && (
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transform: isDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                            transition: 'transform 0.2s ease',
                        }}
                    >
                        <CaretIcon colour={COLORS.GREY} height="13" width="13" />
                    </div>
                )}
            </div>

            {isDropdownOpen && !isDisabled && (
                <div style={{ position: 'relative' }}>
                    <MultiSelectWithDisabledOptions
                        options={allOptions}
                        disabledOptions={[]}
                        selectedItems={selectedValues}
                        placeholder=""
                        toggleSelection={handleToggleSelection}
                        showInput={false}
                        dropdownStyle={{ width: '300px', left: '0px' }}
                        showAddButton={true}
                        onAddClick={() => {
                            try {
                                // Only call update if values have changed
                                const hasChanged =
                                    initialValuesRef.current.length !== selectedValuesRef.current.length ||
                                    !initialValuesRef.current.every((val) => selectedValuesRef.current.includes(val)) ||
                                    !selectedValuesRef.current.every((val) => initialValuesRef.current.includes(val));

                                if (hasChanged) {
                                    updateMultiSelectDropdown({
                                        row,
                                        column,
                                        selectedValues: selectedValuesRef.current,
                                    });
                                }
                            } catch (err) {}
                        }}
                    />
                </div>
            )}
        </div>
    );
};
